import { reportBelongsToSite } from '../lib/reportSiteIdentity';
/**
 * Shared cascade-delete helpers — pure, no I/O.
 *
 * Mirrors the report-delete cascade (Workflow Prompt 7, src/lib/reportDeleteCascade.ts)
 * up one level: a site can have several reports, each with their own SED,
 * uploaded documents, and simulations. This module computes what would be
 * removed for a given site-delete mode without actually touching Firestore
 * or GCS, so the confirmation dialog can render an accurate preview.
 *
 * Tested in src/services/__tests__/cascadeDelete.test.ts.
 */
import type { SiteEntity } from '../types/site';
import type { SiteRagReport, SiteDocument } from '../types/siteRag';
import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../config/reportTypes';

export type DeleteSiteMode = 'site-only' | 'with-reports' | 'with-everything';

/** Rough estimate for an unsized SED JSON object — used when we can't HEAD GCS. */
export const SED_SIZE_ESTIMATE_BYTES = 50_000;
/** Rough estimate for the report payload JSON in GCS. */
export const REPORT_PAYLOAD_ESTIMATE_BYTES = 250_000;

/* ------------------------------------------------------------------ */
/*  Cross-reference primitives                                         */
/* ------------------------------------------------------------------ */

/**
 * Returns the list of reports (other than the excluded ones) that reference
 * `sedId`. A SED is "still referenced" iff this list is non-empty.
 *
 * The two-arg form mirrors the Workflow Prompt 7 helper; the third arg lets
 * the site-delete pass exclude *all* reports it's about to delete in one shot.
 */
export function findReportsReferencingSed(
  sedId: string,
  allReports: readonly SiteRagReport[],
  excludeReportIds: ReadonlySet<string> = new Set(),
): SiteRagReport[] {
  if (!sedId) return [];
  return allReports.filter(r => r.sedId === sedId && !excludeReportIds.has(r.id));
}

/**
 * Resolve every report attached to a site. Matches by:
 *   1. report.siteId === site.id           (post-migration, primary)
 *   2. site.reportIds includes report.id   (denormalized cache)
 *   3. report.siteName matches site.name   (legacy pre-migration fallback)
 *
 * Deduplicates by report.id.
 */
export function findReportsForSite(
  site: SiteEntity,
  allReports: readonly SiteRagReport[],
): SiteRagReport[] {
  const seen = new Set<string>();
  const out: SiteRagReport[] = [];
  for (const r of allReports) {
    if (seen.has(r.id)) continue;
    const matches = reportBelongsToSite(site, r);
    if (matches) {
      seen.add(r.id);
      out.push(r);
    }
  }
  return out;
}

/* ------------------------------------------------------------------ */
/*  Site-delete impact                                                 */
/* ------------------------------------------------------------------ */

export interface SedCascadeEntry {
  sedId: string;
  /** Reports outside this site that still reference the SED — keeping it on disk. */
  preservedBy: SiteRagReport[];
  /** True iff `preservedBy` is empty AND the SED would be removed in `with-everything`. */
  willBeDeleted: boolean;
}

export interface SiteDeleteImpact {
  /** All reports that belong to this site (resolved via id/cache/name fallback). */
  reports: SiteRagReport[];
  /** Distinct report-type short labels — for the "(Feasibility, Permit)" line. */
  reportTypeLabels: string[];
  /** SED ids referenced by any report in this site, with cross-reference info. */
  seds: SedCascadeEntry[];
  /** SEDs that would survive a `with-everything` because another site references them. */
  preservedSedIds: string[];
  /** Uploaded documents across all site reports, deduped by storagePath || id. */
  documents: SiteDocument[];
  /** simulationCount comes from site.simulationIds.length — the store action queries
   *  Firestore for the actual rows when the user confirms. */
  simulationCount: number;
  /** Per-mode estimated bytes freed. */
  bytesFreed: {
    siteOnly: number;
    withReports: number;
    withEverything: number;
  };
}

/**
 * Compute the cascade preview for a site delete. Pure; no Firestore / Storage I/O.
 *
 * `simulationCount` should be passed by the caller — usually `site.simulationIds.length`.
 * The store action will do the actual Firestore query when it executes the delete;
 * the dialog uses this estimate for the preview.
 */
export function computeSiteDeleteImpact(
  site: SiteEntity,
  allReports: readonly SiteRagReport[],
  simulationCount = site.simulationIds?.length ?? 0,
): SiteDeleteImpact {
  const reports = findReportsForSite(site, allReports);
  const reportIdSet = new Set(reports.map(r => r.id));

  // Distinct report-type labels (display-friendly, sorted for stable rendering).
  const typeIds = new Set<string>();
  for (const r of reports) typeIds.add(r.reportType ?? 'feasibility');
  const reportTypeLabels = Array.from(typeIds)
    .map(id => REPORT_TYPE_REGISTRY[id as ReportTypeId]?.shortLabel ?? id)
    .sort();

  // Distinct SEDs across these reports + cross-reference check.
  const sedIds = new Set<string>();
  for (const r of reports) if (r.sedId) sedIds.add(r.sedId);
  const seds: SedCascadeEntry[] = Array.from(sedIds).map(sedId => {
    const preservedBy = findReportsReferencingSed(sedId, allReports, reportIdSet);
    return { sedId, preservedBy, willBeDeleted: preservedBy.length === 0 };
  });
  const preservedSedIds = seds.filter(s => !s.willBeDeleted).map(s => s.sedId);

  // Deduplicate uploaded documents across reports (by storagePath, fallback id).
  const docMap = new Map<string, SiteDocument>();
  for (const r of reports) {
    for (const d of r.documents ?? []) {
      const key = d.storagePath || d.id;
      if (!docMap.has(key)) docMap.set(key, d);
    }
  }
  const documents = Array.from(docMap.values());

  // Per-mode byte estimates.
  const reportPayloadTotal = reports.length * REPORT_PAYLOAD_ESTIMATE_BYTES;
  const docTotal = documents.reduce((s, d) => s + (d.sizeBytes || 0), 0);
  const sedDeletableTotal = seds.filter(s => s.willBeDeleted).length * SED_SIZE_ESTIMATE_BYTES;

  return {
    reports,
    reportTypeLabels,
    seds,
    preservedSedIds,
    documents,
    simulationCount,
    bytesFreed: {
      siteOnly: 0,
      withReports: reportPayloadTotal,
      withEverything: reportPayloadTotal + sedDeletableTotal + docTotal,
    },
  };
}

/* ------------------------------------------------------------------ */
/*  Result of an executed site-delete                                  */
/* ------------------------------------------------------------------ */

export interface DeleteSiteResult {
  deletedReports: number;
  deletedSeds: number;
  /** SEDs left because another report (outside this site) references them. */
  preservedSeds: number;
  deletedDocuments: number;
  deletedSimulations: number;
  storageFreedBytes: number;
  errors: string[];
}

/** Format bytes for human-readable display in the dialog/toast. Mirrors reportDeleteCascade. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
